"use client";

import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Stripe, loadStripe } from "@stripe/stripe-js";
import { useEffect, useMemo, useRef, useState } from "react";
import RainforestSkeleton from "@/components/rainforest/rainforest-skeleton";
import { Button } from "@/components/ui";
import { assertMountedSecret } from "@/lib/payments/assert-mounted-secret";
import { StripeCardPreview } from "@/lib/payments/card-funding";
import { DeclineAudience, PaymentDecline, normalizeDecline } from "@/lib/payments/decline-reason";
import { registerPaymentSurface } from "@/lib/payments/payment-surface-registry";
import { isSessionAlreadyConsumed } from "@/lib/payments/session-consumed";
import { useSurfaceLoadRetry } from "@/lib/payments/use-surface-load-retry";
import { cx } from "@/lib/tailwind-utils";
import { PaymentProcessor } from "@/types/auth";
import { PaymentMethodType } from "@/types/enums/payment";
import { useStripeAppearance } from "./use-stripe-appearance";

/**
 * Stripe's half of the card-entry contract.
 *
 * The shape mirrors `RainforestTerminal` deliberately — same callbacks, same
 * registration with the payment-surface registry, same fire-and-forget submit —
 * so the thirteen call sites that ask "submit the card form" keep working
 * without knowing which processor rendered it.
 *
 * Two things differ underneath, and both are why the surface exists rather
 * than the call sites branching:
 *
 * - Rainforest's `submit()` is synchronous and reports through DOM events.
 *   Stripe's `confirmPayment` is a promise. The registry's `submit` stays
 *   synchronous and this component fires the promise, resolving it into the
 *   same callbacks — so `onSuccess`/`onFailure` remain the only contract.
 * - Direct charges make the clinic the merchant of record, so Elements has to
 *   be initialised *as* the connected account (`stripeAccount`). Without it the
 *   intent simply is not visible and the form fails with an unhelpful error.
 */

interface StripePaymentSurfaceProps {
  publishableKey: string;
  stripeAccount: string;
  clientSecret: string;
  onSuccess: (paymentIntentId: string) => void;
  /**
   * The message to show, and the classified decline behind it. The second
   * argument matters: `RainforestTerminal` has always passed it, and the
   * virtual terminal keys its heading off it - without it a genuine Stripe card
   * decline was titled "Payment could not be processed" rather than shown as a
   * decline.
   */
  onFailure: (message: string, decline?: PaymentDecline) => void;
  onValid?: () => void;
  /**
   * Tenant policy, not capability. Stripe already hides a wallet whose domain
   * is not registered on the connected account, so this only has to carry the
   * decision a clinic made - "we do not take Apple Pay" - which Stripe cannot
   * know. Defaults to allowed, matching `patient_checkout_allow_apple_pay`.
   */
  allowApplePay?: boolean;
  allowGooglePay?: boolean;
  /**
   * Offer Link - Stripe's own saved-card wallet, which appears as an inline
   * "save my info for 1-click checkout" signup in the card form.
   *
   * Defaults to off, which is the opposite polarity to the wallets above and
   * deliberate. Link enrols *the person at the keyboard*, and on the staff
   * surfaces that is a staff member typing a patient's card: the signup would
   * offer to file the patient's card against the clinic's own email address.
   * A surface that has not said it is patient-facing is assumed not to be.
   *
   * Nothing server-side has to change for this. Our intents are minted with
   * `payment_method_types` of card and us_bank_account only (see
   * `_METHOD_TYPES` in `clients/payments/stripe_adapter.py`), so Link can only
   * ever reach the form as a wallet - which is exactly what this suppresses.
   */
  allowLink?: boolean;
  /**
   * Who reads a refusal. The default copy is written for staff at a desk ("the
   * patient's financial institution", "Ask the patient to…"); on a page the
   * patient was sent to, that told them to ask themselves.
   */
  audience?: DeclineAudience;
  /**
   * Brand and funding for the card that has just been typed, from a
   * ConfirmationToken's `payment_method_preview`. This is Stripe's equivalent of
   * Rainforest's `bin-lookup` event and it is what makes dual pricing work on
   * Stripe: without it `debitOrCredit` never leaves its initial CREDIT and a
   * surcharging clinic adds the fee to debit cards, which card-network rules
   * forbid.
   *
   * Called with `null` when the probe could not answer, which reads the same as
   * a failed BIN lookup.
   *
   * Omit it and no ConfirmationToken is minted at all - there is no point
   * paying for a round trip whose answer nobody reads.
   */
  onCardFunding?: (card: StripeCardPreview | null) => void;
  /**
   * Which method the form is on, as the patient picks it. A surcharging clinic
   * needs this: the fee is built into the intent before anyone picks card or
   * bank, and a bank account must not carry it.
   */
  onMethodTypeChange?: (methodType: PaymentMethodType | null) => void;
  /**
   * The method the caller last heard about. Changing the fee mints a new
   * intent and remounts the form, which would otherwise reopen on card and
   * undo a bank choice the moment it was made.
   */
  methodType?: PaymentMethodType | null;
  /**
   * The intent had already been confirmed and had already succeeded.
   *
   * Not a failure, and not a guess: Stripe says it structurally -
   * `payment_intent_unexpected_state` carrying an intent whose status is
   * `succeeded` - which is why `isSessionAlreadyConsumed` needs no string
   * matching on this processor. Without this the second confirm of a payment
   * that went through was reported to the patient as a decline, and the
   * Rainforest surface has had the same callback all along.
   *
   * Omit it and the surface reports success with the settled intent's id
   * instead, which is what the payment actually was.
   */
  onSessionConsumed?: () => void;
  className?: string;
}

/** Stripe's payment method types, in the console's terms. Wallets ride on card. */
const STRIPE_METHOD_TYPES: Record<string, PaymentMethodType> = {
  card: PaymentMethodType.CARD,
  us_bank_account: PaymentMethodType.ACH
};

/**
 * `loadStripe` injects a script tag and is expensive; Stripe's own guidance is
 * to call it once outside render. Memoised per (key, account) because a tenant
 * can hold merchants on more than one connected account, and an instance built
 * for one account cannot confirm the other's intent.
 */
const stripeInstances = new Map<string, Promise<Stripe | null>>();
export function getStripe(publishableKey: string, stripeAccount: string): Promise<Stripe | null> {
  const key = `${publishableKey}::${stripeAccount}`;
  let instance = stripeInstances.get(key);
  if (!instance) {
    instance = loadStripe(publishableKey, {
      stripeAccount
    });
    // Evicted on failure so "Try again" can actually try again. A rejected
    // promise cached forever means every later mount - including the remount
    // the retry button triggers - re-reads the same failure and returns to the
    // skeleton, which is the one case the retry exists for. Caching the
    // *success* is the point of this map; caching a network blip is not.
    instance.catch(() => {
      if (stripeInstances.get(key) === instance) stripeInstances.delete(key);
    });
    stripeInstances.set(key, instance);
  }
  return instance;
}

/** Test helper. Never call this from application code. */
export function __resetStripeInstances(): void {
  stripeInstances.clear();
}
function StripeCardForm({
  clientSecret,
  onSuccess,
  onFailure,
  onValid,
  onCardFunding,
  onMethodTypeChange,
  methodType,
  onSessionConsumed,
  onLoaded,
  onRetry,
  didNotLoad,
  allowApplePay,
  allowGooglePay,
  allowLink,
  audience
}: Pick<StripePaymentSurfaceProps, "clientSecret" | "onSuccess" | "onFailure" | "onValid" | "onCardFunding" | "onMethodTypeChange" | "methodType" | "onSessionConsumed" | "allowApplePay" | "allowGooglePay" | "allowLink" | "audience"> & {
  onLoaded: () => void;
  onRetry: () => void;
  didNotLoad: boolean;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isReady, setIsReady] = useState(false);

  // Captured once, on mount, and never updated. That is the whole point: the
  // enclosing `Elements` group is keyed by the secret, so a new session
  // remounts this component and this ref along with it. If the key is ever
  // removed or weakened, this ref keeps the *old* secret while the prop moves
  // on - and the assert at confirm time has something real to compare.
  const mountedSecret = useRef(clientSecret).current;

  // Refs, not deps: the registration must survive a re-render (re-registering
  // would push this surface back to the top of the stack and steal submit from
  // a dialog opened over it), so the effect below runs once and reads current
  // values through these.
  const latest = useRef({
    stripe,
    elements,
    clientSecret,
    onSuccess,
    onFailure,
    onCardFunding,
    onSessionConsumed,
    audience
  });
  latest.current = {
    stripe,
    elements,
    clientSecret,
    onSuccess,
    onFailure,
    onCardFunding,
    onSessionConsumed,
    audience
  };

  // The watchdog has given up and the retry affordance is on screen in place
  // of the card fields. The surface stays registered - unregistering would
  // hand submit to whatever is underneath - so submit has to refuse here
  // rather than confirm an empty form and report it as a decline.
  const failedToLoad = useRef(false);
  failedToLoad.current = didNotLoad && !isReady;

  // Guards a double submit: `confirmPayment` is a network round trip and the
  // Charge button is not always disabled fast enough behind it.
  const inFlight = useRef(false);

  // One funding probe per completed card, not one per keystroke. The element
  // reports `complete` again on every later change - a ZIP edit, an expiry
  // correction - and each of those would otherwise mint another
  // ConfirmationToken. Cleared when the form goes incomplete, so replacing the
  // card number re-probes, which is what Rainforest's BIN lookup does.
  const probed = useRef(false);
  // The element repeats its type on every change; callers hear about a switch.
  const reportedMethodType = useRef(methodType ?? null);
  // Read once: the order only matters as the form opens, and this component
  // remounts with every new intent.
  const opensOnBank = useRef(methodType === PaymentMethodType.ACH).current;
  // The probe's own in-flight marker. `probeCardFunding` already refuses to
  // start while a confirm is running; without this the reverse was unguarded,
  // so pressing Charge the instant the card number completed ran
  // `confirmPayment` against the same Elements instance as the probe's
  // `elements.submit()` / `createConfirmationToken`. Holding the promise rather
  // than a boolean lets `submit` wait the probe out instead of refusing.
  const probing = useRef<Promise<void> | null>(null);
  const probeCardFunding = async () => {
    const current = latest.current;
    const report = current.onCardFunding;
    if (!report || !current.stripe || !current.elements) return;
    // A confirm already in flight owns the element; a probe alongside it would
    // race the very submission it is meant to inform.
    if (inFlight.current) return;
    try {
      // Required before `createConfirmationToken`, and safe here: the element
      // has just told us it is complete, so validation has nothing to surface.
      const {
        error: submitError
      } = await current.elements.submit();
      if (submitError) {
        report(null);
        return;
      }
      const {
        confirmationToken,
        error
      } = await current.stripe.createConfirmationToken({
        elements: current.elements
      });
      report(error || !confirmationToken ? null : confirmationToken.payment_method_preview?.card ?? null);
    } catch {
      // A probe that fails is not a payment failure. It leaves the surcharge
      // decision exactly where a failed Rainforest BIN lookup leaves it, and
      // the patient can still pay.
      report(null);
    }
  };
  useEffect(() => registerPaymentSurface({
    submit: () => {
      const current_0 = latest.current;
      if (inFlight.current) return;
      if (failedToLoad.current) {
        current_0.onFailure("The card form did not load. Use Try again to reload it.");
        return;
      }
      if (!current_0.stripe || !current_0.elements) {
        current_0.onFailure("The card form is still loading. Please try again in a moment.");
        return;
      }
      // The group is keyed by the secret, so a changed secret should
      // already have remounted this component. If it somehow did not, the
      // next line is the difference between charging the right session and
      // silently settling a spent one.
      try {
        assertMountedSecret(mountedSecret, current_0.clientSecret);
      } catch (error_0) {
        // The thrown text is developer copy ("This is a bug: ..."), and this
        // surface renders on the patient page. Log the detail, show the
        // fixed sentence.
        console.error(error_0);
        current_0.onFailure("The card form is out of step with this payment. Please reload and try again.");
        return;
      }
      inFlight.current = true;
      // Wait out a probe that is mid-flight. It is at most one round trip,
      // it is already running by the time the button can be pressed, and
      // confirming underneath it is the race the probe's own guard warns
      // about. `inFlight` is set first, so the probe cannot start again.
      const pendingProbe = probing.current ?? Promise.resolve();
      void pendingProbe.catch(() => undefined).then(() => current_0.stripe!.confirmPayment({
        elements: current_0.elements!,
        // No `clientSecret` here on purpose: `Elements` was created with
        // it, so this is the confirm form Stripe documents for that flow
        // and there is no second copy to fall out of step with.
        //
        // Nothing in this flow redirects: `allow_redirects: "never"` is
        // set on the intent, so a method that would redirect is never
        // offered in the first place.
        redirect: "if_required"
      })).then(({
        error: error_1,
        paymentIntent
      }) => {
        if (error_1) {
          // The intent had already been confirmed and had already
          // succeeded - a retry, a double click, a remount. The money
          // moved; the second confirm is the thing that failed. Telling
          // the patient their card was declined here is how a paid
          // charge gets paid twice.
          if (isSessionAlreadyConsumed(PaymentProcessor.STRIPE, {
            error: error_1
          })) {
            if (current_0.onSessionConsumed) {
              current_0.onSessionConsumed();
              return;
            }
            const settled = (error_1 as {
              payment_intent?: {
                id?: string;
              };
            }).payment_intent;
            if (settled?.id) {
              current_0.onSuccess(settled.id);
              return;
            }
          }
          const declined = normalizeDecline(PaymentProcessor.STRIPE, {
            error: error_1
          }, current_0.audience);
          current_0.onFailure(declined.message, declined);
          return;
        }
        if (!paymentIntent) {
          current_0.onFailure("We could not confirm this payment. Check the patient's charges before retrying.");
          return;
        }
        // `processing` is a success from the form's point of view - the
        // money is moving and the webhook settles it - which is the same
        // contract the Rainforest terminal has.
        if (paymentIntent.status === "succeeded" || paymentIntent.status === "processing") {
          current_0.onSuccess(paymentIntent.id);
          return;
        }
        const declined_0 = normalizeDecline(PaymentProcessor.STRIPE, {
          error: paymentIntent.last_payment_error
        }, current_0.audience);
        current_0.onFailure(declined_0.message, declined_0);
      }).catch(() => {
        current_0.onFailure("We could not reach the payment processor. Please try again.");
      }).finally(() => {
        inFlight.current = false;
      });
    }
  }), []);
  if (didNotLoad && !isReady) {
    return <div className="flex flex-col items-start gap-2 py-4">
        <p className="text-body text-status-error-text">
          The card form did not finish loading.
        </p>
        <Button variant="primary" onClick={onRetry}>
          <span>Try again</span>
        </Button>
      </div>;
  }
  return <>
      {!isReady && <RainforestSkeleton padding="md" />}
      <div className={cx(!isReady && "hidden")}>
        <PaymentElement options={{
        // Rainforest renders the card fields immediately; the Payment
        // Element defaults to an accordion with every method collapsed, so
        // on a Stripe merchant staff had to click "Card" before they could
        // type. Expanding the first item is the closer match, and with one
        // method configured Stripe renders it expanded either way.
        layout: {
          type: "accordion",
          defaultCollapsed: false
        },
        // `defaultCollapsed: false` expands the *first* method, and Stripe
        // orders them itself - this form also offers "US bank account", so
        // "card fields open" was a default rather than a guarantee. The
        // save form offers Card only and needs no pin.
        paymentMethodOrder: opensOnBank ? ["us_bank_account", "card"] : ["card"],
        // "never" is a hard suppression; "auto" leaves it to Stripe, which
        // already accounts for the browser and the connected account's
        // registered domains.
        wallets: {
          applePay: allowApplePay === false ? "never" : "auto",
          googlePay: allowGooglePay === false ? "never" : "auto",
          // Opt-in rather than opt-out - see `allowLink` above for why the
          // staff surfaces must not offer to enrol whoever is typing.
          link: allowLink ? "auto" : "never"
        }
      }} onReady={() => {
        setIsReady(true);
        onLoaded();
      }} onChange={event => {
        const selected = STRIPE_METHOD_TYPES[event.value.type] ?? null;
        if (selected !== reportedMethodType.current) {
          reportedMethodType.current = selected;
          onMethodTypeChange?.(selected);
        }
        if (!event.complete) {
          probed.current = false;
          return;
        }
        onValid?.();
        if (probed.current) return;
        probed.current = true;
        const run = probeCardFunding().finally(() => {
          probing.current = null;
        });
        probing.current = run;
        void run;
      }} />
      </div>
    </>;
}
export default function StripePaymentSurface({
  publishableKey,
  stripeAccount,
  clientSecret,
  onSuccess,
  onFailure,
  onValid,
  onCardFunding,
  onMethodTypeChange,
  methodType,
  onSessionConsumed,
  allowApplePay = true,
  allowGooglePay = true,
  allowLink = false,
  audience = "staff",
  className
}: StripePaymentSurfaceProps) {
  const appearance = useStripeAppearance();
  const {
    attempt,
    didNotLoad,
    markLoaded,
    retry
  } = useSurfaceLoadRetry(clientSecret);
  // `attempt` is a dependency, not decoration: only the Elements child is keyed
  // by it, so without this a retry would hand the new child the same promise -
  // and if what failed was the Stripe.js script itself, that promise is already
  // rejected. Paired with the eviction in `getStripe`, re-calling here starts a
  // genuinely new load; when nothing failed, the map still returns the cached
  // instance, so a retry costs nothing.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stripePromise = useMemo(() => getStripe(publishableKey, stripeAccount), [publishableKey, stripeAccount, attempt]);
  return <div className={cx("w-full", className)}>
      {/* Keyed by the secret: Stripe will not let an existing Elements
          instance change `clientSecret`, so a re-minted session has to remount
          rather than silently keep confirming against the spent one. The card
          the patient typed is cleared by that remount, which is the honest
          outcome - the old session is gone.
           `attempt` is in the key for the same reason: a retry that reuses the
          iframes that never finished loading is not a retry. */}
      <Elements key={`${clientSecret}::${attempt}`} stripe={stripePromise} options={{
      clientSecret,
      appearance,
      // Required for `createConfirmationToken`, and only set when a caller
      // actually wants the funding answer. Stripe.js refuses the call
      // outright without it - "To use createPaymentMethod with the Payment
      // Element, your elements instance must be created with
      // paymentMethodCreation: 'manual'" - so leaving it off would make the
      // probe below a silent no-op.
      //
      // It is not free, which is why it is conditional: in manual mode
      // Stripe hides PayPal, Klarna and Amazon Pay. None of those are ever
      // offered here (our intents carry `payment_method_types` of card and
      // us_bank_account), and Apple Pay and Google Pay are unaffected -
      // they are not in the set manual mode suppresses. Confirming with
      // `elements` keeps working: the manual-mode requirement is on the
      // create-a-payment-method family of calls, not on confirm.
      ...(onCardFunding ? {
        paymentMethodCreation: "manual" as const
      } : {})
    }}>
        <StripeCardForm clientSecret={clientSecret} onSuccess={onSuccess} onFailure={onFailure} onValid={onValid} onCardFunding={onCardFunding} onMethodTypeChange={onMethodTypeChange} methodType={methodType} onSessionConsumed={onSessionConsumed} onLoaded={markLoaded} onRetry={retry} didNotLoad={didNotLoad} allowApplePay={allowApplePay} allowGooglePay={allowGooglePay} allowLink={allowLink} audience={audience} />
      </Elements>
    </div>;
}